import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Business,
  Home,
  People,
  Work,
  AttachMoney,
  Inventory,
  AccountCircle,
  Logout,
  PersonAdd,
  Settings as SettingsIcon,
  BarChart,
  DirectionsWalk,
  AccessTime,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import Notifications from '../Notifications/Notifications';

const drawerWidth = 240;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { user, logout } = useAuth();
  const { appName, logo, themeMode, currentSidebarBg, currentSidebarText } = useThemeContext();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    setAnchorEl(null);
  };

  const { can } = useAuth();
  const menuItems = [
    // Dashboard
    ...(can('dashboard.view') ? [{ text: t('sidebar.dashboard'), icon: <Dashboard />, path: '/' }] : []),
    // Work Orders
    ...(can('work-orders.view') ? [{ text: t('sidebar.workOrders'), icon: <Work />, path: '/work-orders' }] : []),
    // Out-of-Site Tasks
    ...(can('out-of-site-tasks.view') ? [{ text: t('sidebar.outOfSiteTasks'), icon: <DirectionsWalk />, path: '/out-of-site-tasks' }] : []),
    // Reports
    ...(can('reports.view') ? [{ text: t('sidebar.reports'), icon: <BarChart />, path: '/reports' }] : []),
    // Attendance
    ...(can('attendance.view') || can('attendance.mark') ? [{ text: t('sidebar.attendance'), icon: <AccessTime />, path: '/attendance' }] : []),
    // Buildings
    ...(can('buildings.view') ? [{ text: t('sidebar.buildings'), icon: <Business />, path: '/buildings' }] : []),
    // Flats
    ...(can('flats.view') ? [{ text: t('sidebar.flats'), icon: <Home />, path: '/flats' }] : []),
    // Roles
    ...(can('roles.view') ? [{ text: t('sidebar.roles'), icon: <PersonAdd />, path: '/roles' }] : []),
    // Users
    ...(can('users.view') ? [{ text: t('sidebar.users'), icon: <People />, path: '/users' }] : []),
    // Technicians
    ...(can('technicians.view') ? [{ text: t('sidebar.technicians'), icon: <PersonAdd />, path: '/technicians' }] : []),
    // Purchase Orders (Expenses)
    ...(can('expenses.view') ? [{ text: t('sidebar.expenses'), icon: <AttachMoney />, path: '/expenses' }] : []),
    // Settings
    ...(can('settings.view') ? [{ text: t('sidebar.settings'), icon: <SettingsIcon />, path: '/settings' }] : []),
  ];

  const drawer = (
    <div>
      <Toolbar
        sx={{
          color: currentSidebarText,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1.5,
          py: .5,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          {logo && (
            <Avatar
              src={logo}
              alt="Logo"
              sx={{ width: 64, height: 64 }}
              variant="rounded"
            />
          )}
          <Typography
            variant="subtitle1"
            noWrap
            sx={{
              fontWeight: 600,
              color: currentSidebarText,
            }}
          >
            {appName || t('app.title')}
          </Typography>
        </Box>
      </Toolbar>
      <Divider sx={{ borderColor: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)' }} />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
              sx={{
                color: currentSidebarText,
                '&.Mui-selected': {
                  backgroundColor: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                  color: currentSidebarText,
                },
                '&:hover': {
                  backgroundColor: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                },
              }}
            >
              <ListItemIcon sx={{ color: currentSidebarText }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text}
                sx={{ color: currentSidebarText }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          {/* Remove site name from main page header, keep spacer so actions stay aligned */}
          <Box sx={{ flexGrow: 1 }} />
          <Notifications />
          {/* Language switcher */}
          <Box sx={{ mr: 2 }}>
            <Button
              color="inherit"
              size="small"
              onClick={() => setLanguage(language === 'en' ? 'ar' : 'en')}
            >
              {language === 'en' ? t('header.language.arabic') : t('header.language.english')}
            </Button>
          </Box>

          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="profile-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              <AccountCircle />
            </Avatar>
          </IconButton>
          <Menu
            id="profile-menu"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
          >
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                {language === 'ar' && user?.name_ar ? user.name_ar : user?.name} ({user?.role})
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: currentSidebarBg,
              color: currentSidebarText,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: currentSidebarBg,
              color: currentSidebarText,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
